import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useCallback, useMemo, useState, useEffect } from 'react';
import { DEFAULT_LAYOUT } from '../../types';
import { useProjectStore } from '../../stores/projectStore';
import { computeActiveStates, isTimelineUnresolved, evaluateBranchCondition } from '../../lib/branchLogic';
import { EventNode } from './EventNode';
import { BranchArrow } from './BranchArrow';
const LANE = DEFAULT_LAYOUT.laneHeight;
const EW = DEFAULT_LAYOUT.eventWidth;
const ES = DEFAULT_LAYOUT.eventSpacing;
const PT = DEFAULT_LAYOUT.paddingTop;
export function TimelineCanvas({ readOnly = false }) {
    const { timelines, events, branches, project, selectedEventId, viewport, setViewport, selectEvent, updateEventStatus, } = useProjectStore();
    const settings = project?.settings;
    const unresolvedSettings = settings?.unresolvedBranchDisplay ?? { show: true, opacity: 'dimmed' };
    const svgRef = useRef(null);
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });
    const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
    const [hasAutoCentered, setHasAutoCentered] = useState(false);
    // Sort timelines by order
    const sortedTimelines = useMemo(() => [...timelines].sort((a, b) => a.order - b.order), [timelines]);
    // Dynamic Padding Left based on labels
    const dynamicPL = useMemo(() => {
        if (sortedTimelines.length === 0)
            return DEFAULT_LAYOUT.paddingLeft;
        const maxChars = Math.max(...sortedTimelines.map(t => t.name.length), 8);
        // Approx 6.5px per char (at 11px font) + 8px start + 16px padding + 24px gap
        return Math.max(maxChars * 6.5 + 48, DEFAULT_LAYOUT.paddingLeft);
    }, [sortedTimelines]);
    // Map each event ID to its global index for X positioning (grouped by date)
    const globalXMap = useMemo(() => {
        if (events.length === 0)
            return new Map();
        // 1. Get unique dates and sort them
        const uniqueDates = Array.from(new Set(events.map(e => e.dateValue))).sort();
        // 2. Map each date to its chronological index
        const dateToIndex = new Map();
        uniqueDates.forEach((date, idx) => dateToIndex.set(date, idx));
        // 3. Map each event ID to the index of its date
        const map = new Map();
        events.forEach((ev) => {
            const idx = dateToIndex.get(ev.dateValue) ?? 0;
            map.set(ev.id, idx);
        });
        return map;
    }, [events]);
    // Active states (timelines and specific events)
    const { activeTimelines, inactiveEvents } = useMemo(() => computeActiveStates(timelines, branches, events), [timelines, branches, events]);
    // Layout: compute pixel positions for each event based on GLOBAL chronological order
    const eventPositions = useMemo(() => {
        const map = new Map();
        sortedTimelines.forEach((tl, tlIdx) => {
            if (tl.collapsed)
                return;
            const tlEvents = events.filter((e) => e.timelineId === tl.id);
            const cy = PT + tlIdx * LANE + LANE / 2;
            tlEvents.forEach((ev) => {
                const globalIdx = globalXMap.get(ev.id) ?? 0;
                const cx = dynamicPL + globalIdx * ES + EW / 2;
                map.set(ev.id, { cx, cy });
            });
        });
        return map;
    }, [sortedTimelines, events, dynamicPL, globalXMap]);
    // Auto-center helper
    const centerView = useCallback(() => {
        if (events.length === 0 || containerSize.w === 0)
            return;
        const positions = Array.from(eventPositions.values());
        if (positions.length === 0)
            return;
        const minX = 0; // Start from labels
        const maxX = Math.max(...positions.map(p => p.cx)) + EW / 2;
        const minY = Math.min(...positions.map(p => p.cy)) - LANE / 2;
        const maxY = Math.max(...positions.map(p => p.cy)) + LANE / 2;
        const contentW = maxX - minX;
        const contentH = maxY - minY;
        // Calculate scale to fit content with some padding
        const scaleW = (containerSize.w * 0.85) / contentW;
        const scaleH = (containerSize.h * 0.85) / contentH;
        const newScale = Math.min(Math.max(Math.min(scaleW, scaleH), 0.5), 1.2);
        // Calculate translation to center
        const newX = (containerSize.w / 2) - (minX + contentW / 2) * newScale;
        const newY = (containerSize.h / 2) - (minY + contentH / 2) * newScale;
        setViewport({ x: newX, y: newY, scale: newScale });
    }, [events.length, containerSize, eventPositions, setViewport]);
    // Center on initial load or project change
    useEffect(() => {
        if (events.length > 0 && containerSize.w > 0 && !hasAutoCentered) {
            centerView();
            setHasAutoCentered(true);
        }
    }, [events.length, containerSize.w, hasAutoCentered, centerView]);
    // Observe container size
    useEffect(() => {
        const el = svgRef.current?.parentElement;
        if (!el)
            return;
        const ro = new ResizeObserver((entries) => {
            const { width, height } = entries[0].contentRect;
            setContainerSize({ w: width, h: height });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    // SVG canvas total size
    const canvasW = useMemo(() => {
        const positions = Array.from(eventPositions.values());
        const maxX = positions.length > 0 ? Math.max(...positions.map((p) => p.cx), 0) : 0;
        return Math.max(maxX + EW + 40, containerSize.w);
    }, [eventPositions, containerSize.w]);
    const canvasH = useMemo(() => {
        return Math.max(PT + sortedTimelines.length * LANE + PT, containerSize.h);
    }, [sortedTimelines.length, containerSize.h]);
    // Pan & Zoom handlers
    const handleWheel = useCallback((e) => {
        e.preventDefault();
        if (e.ctrlKey) {
            // Zooming (standard pinch-to-zoom on trackpads)
            const scaleDelta = e.deltaY > 0 ? 0.95 : 1.05;
            const newScale = Math.min(Math.max(viewport.scale * scaleDelta, 0.2), 4);
            // Try to zoom towards mouse position
            const svg = svgRef.current;
            if (svg) {
                const rect = svg.getBoundingClientRect();
                const mouseX = e.clientX - rect.left;
                const mouseY = e.clientY - rect.top;
                // Current world coordinates under mouse
                const worldX = (mouseX - viewport.x) / viewport.scale;
                const worldY = (mouseY - viewport.y) / viewport.scale;
                // New viewport to keep world position under mouse
                const newX = mouseX - worldX * newScale;
                const newY = mouseY - worldY * newScale;
                setViewport({ x: newX, y: newY, scale: newScale });
            }
            else {
                setViewport({ scale: newScale });
            }
        }
        else {
            // Panning (trackpad scroll)
            setViewport({
                x: viewport.x - e.deltaX,
                y: viewport.y - e.deltaY
            });
        }
    }, [viewport, setViewport]);
    // Touch handling
    const [lastTouch, setLastTouch] = useState(null);
    const [lastDist, setLastDist] = useState(null);
    const handleTouchStart = useCallback((e) => {
        if (e.touches.length === 1) {
            setLastTouch({ x: e.touches[0].clientX, y: e.touches[0].clientY });
            setLastDist(null);
        }
        else if (e.touches.length === 2) {
            const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
            setLastDist(d);
            setLastTouch({
                x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
                y: (e.touches[0].clientY + e.touches[1].clientY) / 2
            });
        }
    }, []);
    const handleTouchMove = useCallback((e) => {
        e.preventDefault();
        if (e.touches.length === 1 && lastTouch) {
            const touch = e.touches[0];
            setViewport({
                x: viewport.x + (touch.clientX - lastTouch.x),
                y: viewport.y + (touch.clientY - lastTouch.y)
            });
            setLastTouch({ x: touch.clientX, y: touch.clientY });
        }
        else if (e.touches.length === 2 && lastDist && lastTouch) {
            const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
            const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
            const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
            const zoomFactor = d / lastDist;
            const newScale = Math.min(Math.max(viewport.scale * zoomFactor, 0.2), 4);
            const svg = svgRef.current;
            if (svg) {
                const rect = svg.getBoundingClientRect();
                const mouseX = centerX - rect.left;
                const mouseY = centerY - rect.top;
                const worldX = (mouseX - viewport.x) / viewport.scale;
                const worldY = (mouseY - viewport.y) / viewport.scale;
                const newX = mouseX - worldX * newScale;
                const newY = mouseY - worldY * newScale;
                setViewport({ x: newX, y: newY, scale: newScale });
            }
            setLastDist(d);
            setLastTouch({ x: centerX, y: centerY });
        }
    }, [viewport, lastTouch, lastDist, setViewport]);
    const handleTouchEnd = useCallback(() => {
        setLastTouch(null);
        setLastDist(null);
    }, []);
    const handleMouseDown = useCallback((e) => {
        if (e.button !== 1 && !(e.button === 0 && e.altKey))
            return;
        e.preventDefault();
        setIsPanning(true);
        setPanStart({ x: e.clientX - viewport.x, y: e.clientY - viewport.y });
    }, [viewport]);
    const handleMouseMove = useCallback((e) => {
        if (!isPanning)
            return;
        setViewport({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    }, [isPanning, panStart, setViewport]);
    const handleMouseUp = useCallback(() => setIsPanning(false), []);
    const handleEventClick = useCallback((ev) => selectEvent(ev.id), [selectEvent]);
    const handleStatusCycle = useCallback((ev) => {
        const cycle = {
            unchecked: 'success',
            success: 'failure',
            failure: 'skipped',
            skipped: 'unchecked',
        };
        updateEventStatus(ev.id, cycle[ev.status]);
    }, [updateEventStatus]);
    const handleCanvasClick = useCallback((e) => {
        if (e.target.tagName === 'svg' || e.target.tagName === 'rect') {
            selectEvent(null);
        }
    }, [selectEvent]);
    // Touch and wheel gesture override for mobile/trackpad browsers
    useEffect(() => {
        const svg = svgRef.current;
        if (!svg)
            return;
        const preventDefault = (e) => {
            // For wheel, only prevent if it's horizontal scroll (often used for back/forward)
            // or if ctrlKey is held (zoom)
            if (e instanceof WheelEvent) {
                if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.ctrlKey) {
                    e.preventDefault();
                }
            }
            else if (e.touches.length <= 2) {
                e.preventDefault();
            }
        };
        svg.addEventListener('touchstart', preventDefault, { passive: false });
        svg.addEventListener('touchmove', preventDefault, { passive: false });
        svg.addEventListener('wheel', preventDefault, { passive: false });
        return () => {
            svg.removeEventListener('touchstart', preventDefault);
            svg.removeEventListener('touchmove', preventDefault);
            svg.removeEventListener('wheel', preventDefault);
        };
    }, []);
    return (_jsxs("div", { className: "relative w-full h-full overflow-hidden bg-gray-50 dark:bg-neutral-950", children: [_jsx("div", { className: "absolute bottom-6 right-6 z-10 flex flex-col gap-1.5", children: [
                    { label: '+', action: () => setViewport({ scale: Math.min(viewport.scale * 1.2, 3) }) },
                    { label: '–', action: () => setViewport({ scale: Math.max(viewport.scale * 0.8, 0.3) }) },
                    { label: '⌂', action: () => centerView() },
                ].map(({ label, action }) => (_jsx("button", { onClick: action, className: "w-8 h-8 flex items-center justify-center rounded-lg bg-white/80 dark:bg-neutral-800/80 backdrop-blur border border-gray-200 dark:border-neutral-700 text-gray-700 dark:text-gray-300 text-sm font-medium shadow-sm hover:bg-white dark:hover:bg-neutral-800 transition-colors", children: label }, label))) }), _jsx("svg", { ref: svgRef, width: "100%", height: "100%", onWheel: handleWheel, onMouseDown: handleMouseDown, onMouseMove: handleMouseMove, onMouseUp: handleMouseUp, onMouseLeave: handleMouseUp, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd, onClick: handleCanvasClick, style: { cursor: isPanning ? 'grabbing' : 'default', touchAction: 'none' }, children: _jsxs("g", { transform: `translate(${viewport.x}, ${viewport.y}) scale(${viewport.scale})`, children: [_jsx("pattern", { id: "grid", x: "0", y: "0", width: ES, height: LANE, patternUnits: "userSpaceOnUse", children: _jsx("circle", { cx: 0, cy: 0, r: 1.5, fill: "#e5e7eb", opacity: 0.6 }) }), _jsx("rect", { x: 0, y: 0, width: canvasW, height: canvasH, fill: "url(#grid)" }), sortedTimelines.map((tl, idx) => {
                            const cy = PT + idx * LANE + LANE / 2;
                            const isUnresolved = isTimelineUnresolved(tl.id, branches, events);
                            if (!unresolvedSettings.show && isUnresolved)
                                return null;
                            const alpha = isUnresolved && unresolvedSettings.opacity === 'dimmed' ? 0.4 : 1;
                            const tlEvents = events
                                .filter((e) => e.timelineId === tl.id)
                                .sort((a, b) => a.dateValue.localeCompare(b.dateValue));
                            return (_jsxs("g", { opacity: alpha, children: [_jsx("rect", { x: 0, y: PT + idx * LANE, width: canvasW, height: LANE, fill: idx % 2 === 0 ? 'rgba(249,250,251,0.5)' : 'rgba(255,255,255,0.5)' }), _jsxs("g", { transform: `translate(8, ${cy})`, children: [_jsx("rect", { x: 0, y: -13, width: tl.name.length * 6.5 + 16, height: 26, rx: 6, fill: tl.color, opacity: 0.12 }), _jsx("text", { y: 4, x: 8, fontSize: 11, fontWeight: 700, fill: tl.color, style: { userSelect: 'none' }, children: tl.name })] }), tlEvents.length >= 2 && (() => {
                                        const indices = tlEvents.map(ev => globalXMap.get(ev.id) ?? 0);
                                        const minIdx = Math.min(...indices);
                                        const maxIdx = Math.max(...indices);
                                        return (_jsx("line", { x1: dynamicPL + minIdx * ES + EW / 2, y1: cy, x2: dynamicPL + maxIdx * ES + EW / 2, y2: cy, stroke: tl.color, strokeWidth: 2, opacity: 0.3, strokeDasharray: isUnresolved ? '6,3' : undefined }));
                                    })(), tlEvents.map((ev) => {
                                        const pos = eventPositions.get(ev.id);
                                        if (!pos)
                                            return null;
                                        const evUnresolved = isUnresolved || inactiveEvents.has(ev.id);
                                        const isOnActivePath = activeTimelines.has(tl.id) && !inactiveEvents.has(ev.id);
                                        return (_jsx(EventNode, { event: ev, x: pos.cx, y: pos.cy, isSelected: selectedEventId === ev.id, isEditing: false, isUnresolved: evUnresolved, unresolvedOpacity: unresolvedSettings.opacity, isActive: isOnActivePath, readOnly: readOnly, onClick: handleEventClick, onStatusCycle: handleStatusCycle }, ev.id));
                                    })] }, tl.id));
                        }), branches.map((branch) => {
                            const sourceEv = events.find((e) => e.id === branch.sourceEventId);
                            const sourceTl = timelines.find((t) => t.id === branch.sourceTimelineId);
                            const targetTl = timelines.find((t) => t.id === branch.targetTimelineId);
                            if (!sourceEv || !sourceTl || !targetTl)
                                return null;
                            // A branch is visible if the target timeline is not hidden by unresolved settings
                            const isTlUnresolved = isTimelineUnresolved(branch.targetTimelineId, branches, events);
                            if (!unresolvedSettings.show && isTlUnresolved)
                                return null;
                            const sourcePos = eventPositions.get(branch.sourceEventId);
                            const targetTlIdx = sortedTimelines.findIndex((t) => t.id === branch.targetTimelineId);
                            const sourceTlIdx = sortedTimelines.findIndex((t) => t.id === branch.sourceTimelineId);
                            if (!sourcePos || targetTlIdx === -1 || sourceTlIdx === -1)
                                return null;
                            const targetTlEvents = events
                                .filter((e) => e.timelineId === branch.targetTimelineId)
                                .sort((a, b) => a.position.x - b.position.x);
                            const firstTargetPos = targetTlEvents[0]
                                ? eventPositions.get(targetTlEvents[0].id)
                                : null;
                            const toX = firstTargetPos?.cx ?? dynamicPL + EW / 2;
                            const toY = firstTargetPos?.cy ?? PT + targetTlIdx * LANE + LANE / 2;
                            // Individual branch resolution (ONE is active)
                            const isBranchResolved = evaluateBranchCondition(branch, events);
                            const EH = DEFAULT_LAYOUT.eventHeight;
                            return (_jsx(BranchArrow, { id: branch.id, fromX: sourcePos.cx, fromY: sourcePos.cy + EH / 2, toX: toX, toY: toY - EH / 2, color: sourceTl.color, label: branch.label, isResolved: isBranchResolved, opacity: unresolvedSettings.opacity }, branch.id));
                        })] }) })] }));
}
